import React from 'react';

/**
 * Avatar - circular monogram avatar with deterministic background color.
 *
 * Props:
 *   name   - person's full name (used to derive initials)
 *   email  - email or any stable string used to pick the background color hash
 *   size   - 'sm' | 'md' | 'lg'   (default 'md')
 *   className - extra Tailwind classes
 *   title  - optional tooltip text (defaults to name || email)
 *
 * The color is chosen deterministically from a fixed 8-color palette by
 * hashing the (email || name) string. Same input always renders the same
 * color across pages.
 */

const SIZES = {
  sm: 'w-7  h-7  text-[10px]',
  md: 'w-9  h-9  text-xs',
  lg: 'w-14 h-14 text-base',
};

// 8-color palette - kept in sync between table rows, panel header and AppShell.
const PALETTE = [
  'bg-teal-600',
  'bg-teal-700',
  'bg-navy-700',
  'bg-emerald-600',
  'bg-amber-600',
  'bg-rose-600',
  'bg-cyan-600',
  'bg-navy-600',
];

/** Cheap, stable string hash -> palette index. */
function colorFor(seed) {
  const s = (seed || '').toLowerCase();
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  }
  return PALETTE[Math.abs(h) % PALETTE.length];
}

/** Derive 1-2 letter initials from a name (or email localpart fallback). */
function initialsFor(name, email) {
  const source = (name && name.trim()) || (email || '').split('@')[0] || '?';
  const parts  = source.replace(/[._-]+/g, ' ').trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export default function Avatar({
  name,
  email,
  size = 'md',
  className = '',
  title,
}) {
  const sizeClass  = SIZES[size] || SIZES.md;
  const colorClass = colorFor(email || name);
  const initials   = initialsFor(name, email);

  return (
    <span
      title={title || name || email}
      className={[
        sizeClass,
        colorClass,
        'rounded-full text-white font-semibold flex items-center justify-center',
        'shrink-0 select-none shadow-sm',
        className,
      ].join(' ')}
      aria-label={name || email || 'Avatar'}
    >
      {initials}
    </span>
  );
}
